import { useState, useRef, useCallback, useEffect } from 'react';
import { useGender } from '@/contexts/GenderContext';
import { ChevronDown } from 'lucide-react';

function getInitialPos(): { x: number; y: number } {
  try {
    const saved = localStorage.getItem('gender_toggle_pos');
    if (saved) {
      const { x, y } = JSON.parse(saved);
      if (typeof x === 'number' && typeof y === 'number') {
        return {
          x: Math.max(0, Math.min(window.innerWidth - 100, x)),
          y: Math.max(60, Math.min(window.innerHeight - 120, y)),
        };
      }
    }
  } catch { /* ignore */ }
  return { x: window.innerWidth - 120, y: window.innerHeight - 180 };
}

function getSiteLogos() {
  try {
    const c = JSON.parse(localStorage.getItem('funcomunion_site_config') || '{}');
    return { logoNino: c.logoNinoUrl || null, logoNina: c.logoNinaUrl || null, logoTexto: c.logoTextoUrl || null };
  } catch { return { logoNino: null, logoNina: null, logoTexto: null }; }
}

export function GenderToggle() {
  const { genero, setGenero } = useGender();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('gender_toggle_collapsed') === 'true');
  const logos = getSiteLogos();
  const posRef = useRef(getInitialPos());
  const [pos, setPos] = useState(posRef.current);
  const dragRef = useRef({ startX: 0, startY: 0, startPosX: 0, startPosY: 0, active: false });

  // Este panel flota por encima de todo, incluidos los modales. En pantallas
  // de 360 a 414 px (iPhone 14, iPhone Plus y casi cualquier Android) caía
  // justo encima del botón "Siguiente" del configurador: se veía activo pero
  // no se podía pulsar, así que no había forma de pasar de paso. Comprobado en
  // producción. Mientras haya un modal abierto, el panel se retira.
  const [modalAbierto, setModalAbierto] = useState(false);
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const mirar = () => setModalAbierto(!!document.querySelector('[role="dialog"]'));
    mirar();
    const observador = new MutationObserver(mirar);
    observador.observe(document.body, { childList: true, subtree: true });
    return () => observador.disconnect();
  }, []);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startY: e.clientY, startPosX: posRef.current.x, startPosY: posRef.current.y, active: true };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }, []);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragRef.current.active) return;
    const newX = Math.max(0, Math.min(window.innerWidth - 100, dragRef.current.startPosX + (e.clientX - dragRef.current.startX)));
    const newY = Math.max(60, Math.min(window.innerHeight - 120, dragRef.current.startPosY + (e.clientY - dragRef.current.startY)));
    posRef.current = { x: newX, y: newY };
    setPos({ x: newX, y: newY });
  }, []);

  const handlePointerUp = useCallback(() => {
    if (!dragRef.current.active) return;
    dragRef.current.active = false;
    try { localStorage.setItem('gender_toggle_pos', JSON.stringify(posRef.current)); } catch { /* ignore */ }
  }, []);

  const toggleCollapse = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem('gender_toggle_collapsed', String(next));
  };

  if (modalAbierto) return null;

  return (
    <aside
      className="fixed z-[70] select-none"
      style={{ left: pos.x, top: pos.y }}
      aria-label="Selector de género de la figura"
    >
      <div className="bg-white rounded-2xl shadow-elegant-lg border border-gray-200 overflow-hidden" style={{ maxWidth: '160px' }}>
        {/* Logo texto as collapse toggle + drag handle */}
        <button
          type="button"
          aria-expanded={!collapsed}
          aria-label={collapsed ? 'Mostrar selector de género' : 'Ocultar selector de género'}
          className="w-full flex items-center gap-1.5 px-3 py-2 cursor-grab active:cursor-grabbing touch-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#D4AF37]"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onClick={(e) => {
            // Only toggle if not dragged
            const dx = Math.abs(e.clientX - dragRef.current.startX);
            const dy = Math.abs(e.clientY - dragRef.current.startY);
            if (dx < 5 && dy < 5) toggleCollapse();
          }}
        >
          {logos.logoTexto ? (
            <img src={logos.logoTexto} alt="" className="h-6 w-auto object-contain" />
          ) : (
            <span className="text-[11px] font-bold text-[#7A5F0A] tracking-wider" aria-hidden="true">FUNCOMUNION</span>
          )}
          <ChevronDown className={`w-3.5 h-3.5 text-gray-300 transition-transform flex-shrink-0 ${collapsed ? '-rotate-90' : ''}`} aria-hidden="true" />
        </button>

        {/* Gender selector — collapsible */}
        {!collapsed && (
          <div className="px-1 pb-1" role="radiogroup" aria-label="Género de la figura">
            <div className="flex rounded-xl overflow-hidden border border-gray-100">
              <button
                type="button"
                role="radio"
                aria-checked={genero === 'nino'}
                onClick={() => setGenero('nino')}
                aria-label="Figura para niño"
                title="Figura para nino"
                className={`flex-1 flex items-center justify-center py-1 transition-all duration-200 ${
                  genero === 'nino'
                    ? 'bg-[#1E3A5F]/10'
                    : 'bg-transparent hover:bg-blue-50 opacity-30'
                }`}
              >
                {logos.logoNino ? (
                  <img src={logos.logoNino} alt="" className="h-12 w-auto object-contain" />
                ) : (
                  <span className={`text-lg font-bold h-12 flex items-center justify-center ${genero === 'nino' ? 'text-[#1E3A5F]' : 'text-gray-300'}`} aria-hidden="true">N</span>
                )}
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={genero === 'nina'}
                onClick={() => setGenero('nina')}
                aria-label="Figura para niña"
                title="Figura para nina"
                className={`flex-1 flex items-center justify-center py-1 transition-all duration-200 ${
                  genero === 'nina'
                    ? 'bg-[#BE185D]/10'
                    : 'bg-transparent hover:bg-pink-50 opacity-30'
                }`}
              >
                {logos.logoNina ? (
                  <img src={logos.logoNina} alt="" className="h-12 w-auto object-contain" />
                ) : (
                  <span className={`text-lg font-bold h-12 flex items-center justify-center ${genero === 'nina' ? 'text-[#BE185D]' : 'text-gray-300'}`} aria-hidden="true">N</span>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}
